import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/api-client';

/**
 * Email template model matching the notification_templates table.
 */
export interface EmailTemplate {
  id: string;
  tenantId: string;
  type: string;
  channel: 'email' | 'in_app' | 'push';
  subjectTemplate: string | null;
  bodyTemplate: string;
  mergeFields: string[];
  isActive: boolean;
  triggerStatuses: string[] | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateEmailTemplatePayload {
  type: string;
  channel: 'email' | 'in_app' | 'push';
  subject_template?: string | null;
  body_template: string;
  merge_fields?: string[];
  is_active?: boolean;
}

export interface UpdateEmailTemplatePayload {
  subject_template?: string | null;
  body_template?: string;
  merge_fields?: string[];
  is_active?: boolean;
  trigger_statuses?: string[] | null;
}

export interface EmailTemplateFilters {
  type?: string;
  channel?: 'email' | 'in_app' | 'push';
  is_active?: boolean;
}

// ─── Query Keys ─────────────────────────────────────────────────────────────

export const emailTemplateKeys = {
  all: ['email-templates'] as const,
  list: (filters?: EmailTemplateFilters) => [...emailTemplateKeys.all, 'list', filters] as const,
  detail: (id: string) => [...emailTemplateKeys.all, 'detail', id] as const,
};

// ─── Mapper ─────────────────────────────────────────────────────────────────

function mapTemplate(raw: Record<string, unknown>): EmailTemplate {
  return {
    id: raw.id as string,
    tenantId: (raw.tenant_id || raw.tenantId || '') as string,
    type: (raw.type || '') as string,
    channel: (raw.channel || 'email') as EmailTemplate['channel'],
    subjectTemplate: (raw.subject_template ?? raw.subjectTemplate ?? null) as string | null,
    bodyTemplate: (raw.body_template || raw.bodyTemplate || '') as string,
    mergeFields: (raw.merge_fields || raw.mergeFields || []) as string[],
    isActive: (raw.is_active ?? raw.isActive ?? true) as boolean,
    triggerStatuses: (raw.trigger_statuses ?? raw.triggerStatuses ?? null) as string[] | null,
    createdAt: (raw.created_at || raw.createdAt || '') as string,
    updatedAt: (raw.updated_at || raw.updatedAt || '') as string,
  };
}

// ─── Hooks ──────────────────────────────────────────────────────────────────

/**
 * Fetch all email templates with optional filtering.
 */
export function useEmailTemplatesList(filters?: EmailTemplateFilters) {
  return useQuery({
    queryKey: emailTemplateKeys.list(filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.type) params.set('type', filters.type);
      if (filters?.channel) params.set('channel', filters.channel);
      if (filters?.is_active !== undefined) params.set('is_active', String(filters.is_active));

      const url = `/notifications/templates${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await apiClient.get<ApiResponse<Record<string, unknown>[]>>(url);
      const raw = response.data.data || [];
      return raw.map(mapTemplate);
    },
  });
}

/**
 * Fetch a single email template by ID.
 */
export function useEmailTemplate(templateId: string) {
  return useQuery({
    queryKey: emailTemplateKeys.detail(templateId),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<Record<string, unknown>>>(
        `/notifications/templates/${templateId}`,
      );
      return mapTemplate(response.data.data);
    },
    enabled: !!templateId,
  });
}

/**
 * Create a new email template.
 */
export function useCreateEmailTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateEmailTemplatePayload) => {
      const response = await apiClient.post<ApiResponse<Record<string, unknown>>>(
        '/notifications/templates',
        payload,
      );
      return mapTemplate(response.data.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: emailTemplateKeys.all });
    },
  });
}

/**
 * Update an existing email template.
 */
export function useUpdateEmailTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateEmailTemplatePayload }) => {
      const response = await apiClient.put<ApiResponse<Record<string, unknown>>>(
        `/notifications/templates/${id}`,
        data,
      );
      return mapTemplate(response.data.data);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: emailTemplateKeys.all });
      queryClient.invalidateQueries({ queryKey: emailTemplateKeys.detail(variables.id) });
    },
  });
}

/**
 * Delete an email template.
 */
export function useDeleteEmailTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/notifications/templates/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: emailTemplateKeys.all });
    },
  });
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Notification type category labels for grouping templates in the UI.
 */
export const TEMPLATE_CATEGORIES: Record<string, string> = {
  auth: 'Authentication & Account',
  user: 'Users & Invitations',
  contact: 'Contacts & Leads',
  job: 'Jobs & Engagements',
  billing: 'Invoicing & Billing',
  sla: 'SLA Monitoring',
  document: 'Documents',
  portal: 'Client Portal',
  system: 'System & Admin',
};

/**
 * Extracts the category prefix from a notification type.
 * Handles both dot notation (e.g., "job.assigned" => "job") and
 * underscore-based types that don't follow dot notation.
 */
export function getTemplateCategory(type: string): string {
  // Map underscore-based types to their categories
  const underscoreTypeMap: Record<string, string> = {
    portal_mfa_code: 'portal',
    portal_invitation: 'portal',
    user_invitation: 'user',
    lead_stale_reminder: 'contact',
    unassigned_prospects_alert: 'contact',
    job_overdue: 'job',
    job_due_date_reminder: 'job',
    task_assigned_to_client: 'job',
    recurring_job_created: 'job',
    reminder_due: 'job',
    sla_warning: 'sla',
    sla_breach: 'sla',
    sla_escalation: 'sla',
    invoice_overdue_reminder: 'billing',
    document_shared: 'document',
    document_malware_detected: 'document',
    portal_document_uploaded: 'document',
  };

  if (underscoreTypeMap[type]) return underscoreTypeMap[type];

  // Dot-notation prefixes that don't map 1:1 to a category key.
  const prefix = type.split('.')[0] || 'system';
  const prefixMap: Record<string, string> = {
    client: 'portal', // e.g. client.message — client-facing portal notifications
  };
  return prefixMap[prefix] ?? prefix;
}

/**
 * Human-readable label for a notification type.
 * Transforms "job.due_reminder_7_days" into "Due Reminder 7 Days".
 */
export function getTemplateTypeLabel(type: string): string {
  const parts = type.split('.');
  const name = parts.slice(1).join(' ') || parts[0];
  return name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Available merge fields per notification category.
 * These represent common fields that can be used in templates.
 */
export const MERGE_FIELDS_BY_CATEGORY: Record<string, string[]> = {
  auth: ['user.first_name', 'user.last_name', 'user.email', 'firm_name', 'reset_link', 'login_url'],
  user: [
    'user.first_name',
    'user.last_name',
    'user.email',
    'firm_name',
    'role_name',
    'invitation_link',
  ],
  contact: [
    'contact.first_name',
    'contact.last_name',
    'assignee_name',
    'firm_name',
    'stage',
    'days_since_change',
    'count',
    'prospect_names',
  ],
  job: [
    'user.first_name',
    'job.title',
    'job.due_date',
    'client.first_name',
    'assignee_name',
    'firm_name',
    'task_title',
    'job_title',
    'title',
  ],
  billing: [
    // Common
    'recipient.first_name',
    'recipient.last_name',
    'client.first_name',
    'client.last_name',
    'client.name',
    'client.email',
    'firm_name',
    'status_label',
    'portal_url',
    'user.first_name',
    // Invoice
    'invoice.number',
    'invoice.total',
    'invoice.subtotal',
    'invoice.tax_amount',
    'invoice.outstanding_balance',
    'invoice.amount_paid',
    'invoice.issue_date',
    'invoice.due_date',
    'invoice.days_overdue',
    'invoice.rounding_amount',
    'invoice.notes',
    // Quote
    'quote.number',
    'quote.total',
    'quote.subtotal',
    'quote.tax_amount',
    'quote.valid_until',
    'quote.rounding_amount',
    'quote.notes',
    // Credit Note
    'credit_note.number',
    'credit_note.amount',
    'credit_note.reason',
    'credit_note.invoice_number',
    // Debit Note
    'debit_note.number',
    'debit_note.amount',
    'debit_note.reason',
    'debit_note.transaction_date',
    'debit_note.due_date',
    // Write-Off
    'write_off.number',
    'write_off.amount',
    'write_off.reason',
    'write_off.transaction_date',
    'write_off.invoice_number',
    // Advance
    'advance.number',
    'advance.total',
    'advance.available_balance',
    'advance.amount_used',
    'advance.transaction_date',
    'advance.due_date',
    'advance.reason',
  ],
  sla: [
    'user.first_name',
    'job.title',
    'target_hours',
    'elapsed_hours',
    'hours_since_breach',
    'escalation_level',
    'warning_threshold_percent',
    'firm_name',
  ],
  document: [
    'client.first_name',
    'client.last_name',
    'document.name',
    'firm_name',
    'portal_link',
    'due_date',
    'job_title',
    'notes',
    'document_count',
    'share_link',
    'expires_at',
    'file_name',
    'virus_name',
    'user.first_name',
  ],
  portal: [
    'client.first_name',
    'client.last_name',
    'firm_name',
    'portal_url',
    'portal_link',
    'invitation_link',
    'expiry_days',
    'expiry_date',
    'code',
    'expires_in_minutes',
  ],
  system: ['user.first_name', 'firm_name', 'report_name', 'login_url', 'tenant.name'],
};
